import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import axios from 'axios';
import Cookies from 'js-cookie';

import { configSettings } from '@/lib/config';
import type { LoginCredentials } from '@/types';
import { authApi } from '@/lib';

/** Store shape ----------------------------------------------------------- */
interface AuthStore {
    // state
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    storeInitialized: boolean;
    // actions
    initialize: () => void;
    login: (creds: LoginCredentials) => Promise<void>;
    logout: () => Promise<void>;
    clearError: () => void;
    setToken: (token: string | null) => void;
    invalidateToken: () => void;
}

export const useAuthStore = create<AuthStore>()(
    devtools(
        persist(
            (set, get) => ({
                token: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
                storeInitialized: false,

                // Add an initialization method for client side
                initialize: () => {
                    if (get().storeInitialized) return;

                    const token = Cookies.get('token') ?? null;

                    set({
                        token,
                        isAuthenticated: !!token,
                        storeInitialized: true,
                    });
                },

                login: async ({ email, password }) => {
                    const formData = new FormData();
                    formData.append('username', email.trim());
                    formData.append('password', password);

                    try {
                        set({ isLoading: true, error: null });

                        const { data } = await authApi.post('/v1/users/login', formData);

                        const token: string = data?.access_token ?? '';
                        Cookies.set('token', token, { expires: 30, path: '/' });

                        set({ token, isAuthenticated: true });
                    } catch (err: unknown) {
                        let message = 'Login failed';

                        if (axios.isAxiosError(err)) {
                            if (err.code === 'ERR_NETWORK') {
                                message = `Network error: Could not connect to auth server at ${configSettings.authApiUrl}. Please check your API configuration.`;
                            } else {
                                message =
                                    err.response?.data?.message ||
                                    err.response?.data?.error ||
                                    `API error: ${err.message}`;
                            }
                        } else if (err instanceof Error) {
                            message = err.message;
                        }

                        console.error('Login error:', {
                            error: err,
                            apiUrl: configSettings.authApiUrl,
                        });
                        set({ error: message });
                        throw err;
                    } finally {
                        set({ isLoading: false });
                    }
                },

                logout: async () => {
                    Cookies.remove('token');

                    set({
                        token: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: null,
                    });
                    await authApi.post('v1/logout', {}, { headers: { Authorization: `Bearer ${get().token}` } });
                },

                clearError: () => set({ error: null }),

                setToken: (token: string | null) => {
                    if (token) {
                        Cookies.set('token', token, { expires: 30, path: '/' });
                        set({ token, isAuthenticated: true });
                    } else {
                        Cookies.remove('token');
                        set({ token: null, isAuthenticated: false });
                    }
                },

                invalidateToken: () => {
                    Cookies.remove('token');

                    set({
                        token: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: 'Session expired. Please login again.',
                    });
                },
                saveToken: (token: string) => {
                    const expires = new Date();
                    expires.setDate(expires.getDate() + 30);
                    Cookies.set('token', token, { expires, path: '/' });
                },
                switchAccount: (token: string) => {
                    const expires = new Date();
                    expires.setDate(expires.getDate() + 30);

                    set({ token });
                    Cookies.set('token', token, { expires: new Date(expires), path: '/' });
                },
            }),
            {
                name: 'auth-storage',
                partialize: state => ({
                    token: state.token,
                    isAuthenticated: state.isAuthenticated,
                }),
            }
        ),
        { name: 'auth-store' }
    )
);
